import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

function ProductCard({ product }) {
  const [imgIdx, setImgIdx] = useState(0);
  const images = product.images || [];
  const hasMultiple = images.length > 1;

  return (
    <motion.div
      variants={fadeUp}
      transition={{ duration: 0.4 }}
      data-testid={`product-card-${product.id}`}
      className="bg-[#141414] rounded-xl border border-white/5 overflow-hidden group card-lift"
    >
      {/* Image */}
      <div className="aspect-square bg-[#1C1C1C] relative overflow-hidden">
        {images.length > 0 ? (
          <>
            <img
              src={images[imgIdx].startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${images[imgIdx]}` : images[imgIdx]}
              alt={product.name}
              data-testid={`product-img-${product.id}`}
              className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
            />
            {hasMultiple && (
              <>
                <button
                  onClick={() => setImgIdx((imgIdx - 1 + images.length) % images.length)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center text-white opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity hover:bg-black/80"
                  data-testid={`product-img-prev-${product.id}`}
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={() => setImgIdx((imgIdx + 1) % images.length)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center text-white opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity hover:bg-black/80"
                  data-testid={`product-img-next-${product.id}`}
                >
                  <ChevronRight size={16} />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {images.map((_, i) => (
                    <button
                      key={i}
                      data-testid={`product-img-dot-${product.id}-${i}`}
                      onClick={() => setImgIdx(i)}
                      className={`w-2 h-2 rounded-full transition-colors ${i === imgIdx ? 'bg-white' : 'bg-white/30'}`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ShoppingBag size={48} className="text-[#FF2A2A]/20" />
          </div>
        )}

        {/* Coming Soon overlay */}
        {product.is_coming_soon && (
          <div className="absolute inset-0 bg-black/20 flex items-center justify-center pointer-events-none">
            <div className="bg-[#FF2A2A] px-5 py-2 rounded-lg -rotate-6 shadow-lg shadow-[#FF2A2A]/20">
              <span className="font-heading text-2xl text-white tracking-wider">COMING SOON</span>
            </div>
          </div>
        )}

        <div className="absolute top-3 right-3 bg-[#141414]/80 backdrop-blur-sm px-3 py-1 rounded-md border border-white/10">
          <span className="font-heading text-lg text-white">${product.price.toFixed(2)}</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-heading text-xl uppercase mb-1 group-hover:text-[#FF2A2A] transition-colors">
          {product.name}
        </h3>
        <p className="font-body text-sm text-[#A1A1AA] leading-relaxed mb-4">{product.description}</p>
        {product.is_coming_soon ? (
          <div className="bg-[#0A0A0A] rounded-lg py-3 text-center border border-white/5">
            <span className="font-body text-xs font-bold text-[#71717A] uppercase tracking-wider">Coming Soon</span>
          </div>
        ) : null}
      </div>
    </motion.div>
  );
}

export default function MerchPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/merch`)
      .then(res => setProducts(res.data || []))
      .catch(err => console.error('Failed to fetch merch:', err))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main data-testid="merch-page" className="pt-16 md:pt-20 min-h-screen pb-32">
      <section className="py-16 md:py-24 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.div variants={fadeUp} transition={{ duration: 0.5 }}>
              <span className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#FF2A2A] block mb-3">OFFICIAL MERCH</span>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl uppercase leading-none mb-4">REP THE SHOW.</h1>
              <p className="font-body text-base text-[#A1A1AA] max-w-lg leading-relaxed">
                Gear up with official Let's Get In To It merch. New drops coming soon.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <div className="section-divider" />

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-[#141414] rounded-xl border border-white/5 animate-pulse">
                <div className="aspect-square bg-[#1C1C1C]" />
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-[#1C1C1C] w-3/4 rounded" />
                  <div className="h-4 bg-[#1C1C1C] w-1/2 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <ShoppingBag size={48} className="mx-auto text-[#FF2A2A]/20 mb-4" />
            <h3 className="font-heading text-2xl uppercase mb-2">NO PRODUCTS YET</h3>
            <p className="font-body text-sm text-[#71717A]">Merch coming soon!</p>
          </div>
        ) : (
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </motion.div>
        )}

        <div className="text-center mt-16">
          <p className="font-body text-sm text-[#71717A]">
            Follow <a href="https://www.instagram.com/letsgetintoitfbe" target="_blank" rel="noopener noreferrer" className="text-[#FF2A2A] hover:underline">@letsgetintoitfbe</a> for merch drop announcements.
          </p>
        </div>
      </section>
    </main>
  );
}
